import React, { useState } from 'react';
import styled from 'styled-components';
import { FormControl } from '@material-ui/core';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import Radio from '@mui/joy/Radio';
import RadioGroup from '@mui/joy/RadioGroup';
import { PT, GB, ES } from 'country-flag-icons/react/3x2';
import { ListItemDecorator } from '@mui/joy';
import * as qs from 'qs';
import { useHistory } from 'react-router-dom';
import i18n from '../i18n/i18n';
import BackButton from '../components/BackButton';
import TopHeader from '../components/TopHeader';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../contexts/UserContext';
import GalpUserMenu from '../components/GalpUserMenu';
import HelexiaChosenIcon from '../images/helexia_chosen_icon.svg';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
  height: 100%;
`;

const Spacer = styled.div`
  margin-bottom: 60px;
`;

const StyledRadio = styled(Radio)<{ $activeColor?: string }>(
  ({ $activeColor = 'rgb(250,194,3)' }) => ({
    '.css-80pwpb-JoyRadio-radio:hover': {
      color: `${$activeColor} !important`,
      borderColor: `${$activeColor} !important`,
    },
    '.css-80pwpb-JoyRadio-radio': {
      color: `${$activeColor} !important`,
      borderColor: `${$activeColor} !important`,
    },
  })
);

const LanguagesView = ({ location }) => {
  const [value, setValue] = useState(i18n.language);
  const { s } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { theme, themeName } = useTheme();
  const { user } = useUser();
  const history = useHistory();
  const isHelexia = themeName === 'helexia';
  const isGalp = themeName === 'galp';

  const galpInitials = (() => {
    const authUser = (user as any)?.user;
    const source = authUser?.name || (authUser?.email || '').split('@')[0];
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    const raw = parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : (parts[0]?.[0] || '');
    return raw.toUpperCase();
  })();

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setValue(newLanguage);
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('pay.elecctro.language', newLanguage);
  };

  if (isGalp) {
    const galpLanguages = [
      { langValue: 'ptPT', label: i18n.t('language_portugal'), flag: '/companies/GALP/lang_flag_pt.svg' },
      { langValue: 'enGB', label: i18n.t('language_english'), flag: '/companies/GALP/lang_flag_en.svg' },
      { langValue: 'esES', label: i18n.t('language_spanish'), flag: '/companies/GALP/lang_flag_es.svg' },
    ];

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        fontFamily: theme.bodyFont,
      }}
      >
        {/* Header */}
        <div style={{
          position: 'relative',
          background: '#f7f4ed',
          minHeight: '94px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 16px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="button"
            aria-label="back"
            onClick={() => history.goBack()}
            style={{
              position: 'absolute',
              left: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 1L1 6L6 11"
                stroke="#0b0b0b"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p style={{
            fontFamily: theme.titleFont,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#0b0b0b',
            margin: 0,
          }}
          >
            {i18n.t('languages_title')}
          </p>
          <GalpUserMenu initials={galpInitials} />
        </div>

        {/* Language cards */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          padding: '40px 16px',
          boxSizing: 'border-box',
        }}
        >
          {galpLanguages.map((lang) => {
            const isSelected = value === lang.langValue;
            return (
              <button
                key={lang.langValue}
                type="button"
                onClick={() => handleLanguageChange({ target: { value: lang.langValue } })}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  width: '100%',
                  height: '47px',
                  padding: '0 16px 0 12px',
                  borderRadius: '14px',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  background: isSelected ? '#ffd6bc' : 'white',
                  border: isSelected ? '4px solid #ff5a00' : '2px solid #bab9ba',
                }}
              >
                <img
                  src={lang.flag}
                  alt=""
                  style={{
                    width: '32px',
                    height: '27px',
                    display: 'block',
                    flexShrink: 0,
                  }}
                />
                <span style={{
                  flex: 1,
                  textAlign: 'left',
                  fontFamily: theme.bodyFont,
                  fontSize: '14px',
                  color: '#0b0b0b',
                }}
                >
                  {lang.label as string}
                </span>
                {isSelected ? (
                  <span style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    border: '2px solid #ff5a00',
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  >
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#ff5a00',
                    }}
                    />
                  </span>
                ) : (
                  <span style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    border: '1.5px solid #bab9ba',
                    boxSizing: 'border-box',
                    flexShrink: 0,
                  }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (isHelexia) {
    const languages = [
      { langValue: 'ptPT', label: i18n.t('language_portugal'), Flag: PT },
      { langValue: 'esES', label: i18n.t('language_spanish'), Flag: ES },
      { langValue: 'enGB', label: i18n.t('language_english'), Flag: GB },
    ];

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
      >
        <TopHeader
          title="language"
          userLogo
          showBackButton
          onBack={() => history.push('/home')}
          backButtonColor={theme.titleColor}
          showShadow
        />
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '30px 12px 66px',
          gap: '40px',
          overflow: 'hidden',
        }}
        >
          <p style={{
            fontFamily: 'Geologica, sans-serif',
            fontWeight: 600,
            fontSize: '32px',
            lineHeight: '32px',
            color: '#004e74',
            textAlign: 'center',
            margin: 0,
            width: '100%',
            whiteSpace: 'pre-line',
          }}
          >
            {i18n.t('choose_language')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
            {languages.map(({ langValue, label, Flag }) => {
              const isSelected = value === langValue;
              return (
                <button
                  key={langValue}
                  type="button"
                  onClick={() => handleLanguageChange({ target: { value: langValue } })}
                  style={{
                    background: isSelected ? '#3f7b97' : '#fcfcfc',
                    border: isSelected ? 'none' : '1px solid #b6b6b6',
                    borderRadius: '12px',
                    height: '36px',
                    padding: '0 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    width: '100%',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{
                    position: 'relative',
                    width: '25px',
                    height: '25px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                  >
                    <Flag style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '40px',
                      height: '40px',
                    }}
                    />
                  </div>
                  <span style={{
                    fontFamily: 'Geologica, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    letterSpacing: '-0.98px',
                    color: isSelected ? 'white' : '#6a6a6a',
                    flex: 1,
                    textAlign: 'left',
                  }}
                  >
                    {label as string}
                  </span>
                  {isSelected && (
                    <img src={HelexiaChosenIcon} alt="selected" style={{ width: '24px', height: '24px' }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Root>
      <div style={{ flex: 1 }}>
        <TopHeader title="languages" userLogo />
        <FormControl
          style={{
            margin: '0 2dvh 2dvh 2dvh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <RadioGroup
            name="people"
            value={value}
            onChange={handleLanguageChange}
          >
            <List
              sx={{
                minWidth: '92dvw',
                '--List-gap': '0.5rem',
                '--ListItem-paddingY': '1rem',
                '--ListItem-radius': '8px',
                '--ListItemDecorator-size': '32px',
              }}
            >
              {[{ value: 'ptPT', label: i18n.t('language_portugal') },
                { value: 'enGB', label: i18n.t('language_english') },
                { value: 'esES', label: i18n.t('language_spanish') }]
                .map((item, index) => (
                  <ListItem
                    variant="outlined"
                    key={item.value}
                    sx={{ boxShadow: 'sm', bgcolor: 'background.body', borderRadius: '16px' }}
                  >
                    <ListItemDecorator>
                      {[<PT title="pt-PT" />, <GB title="en-GB" />, <ES title="es-ES" />][index]}
                    </ListItemDecorator>
                    <StyledRadio
                      overlay
                      value={item.value}
                      label={item.label}
                      $activeColor={theme.buttonColor}
                      sx={{
                        flexGrow: 1,
                        flexDirection: 'row-reverse',
                      }}
                      slotProps={{
                        action: ({ checked }) => ({
                          sx: () => ({
                            ...(checked && {
                              inset: -1,
                              border: '2px solid',
                              borderColor: 'rgba(77,76,77,255)',
                              borderRadius: '16px',
                            }),
                          }),
                        }),
                      }}
                    />
                  </ListItem>
                ))}
            </List>
          </RadioGroup>
          <Spacer />
        </FormControl>
        <BackButton screen={`/home${s ? `?s=${s}` : ''}`} />
      </div>
    </Root>
  );
};

export default LanguagesView;
